import { useState } from 'react'
import type { EligibilityCheckDoc, RecoveryAction, PatientField } from '../types'
import { REJECTION_GUIDANCE } from '../constants/rejectionGuidance'
import { scrubVendorText } from '../utils/scrubVendorText'
import { useSlideout } from '../hooks/useSlideout'
import { IconRefresh, IconChevronDown, IconShield } from './Icons'
import { maxTotalAttempts } from '../../shared/eligibilityDisplayStatus'
import { reasonCopyFor } from '../../shared/terminalState'
import { readTerminal } from '../utils/terminalStateBadge'
import {
  SlideoutHeaderBar,
  SlideoutSummary,
  SlideoutPayerGrid,
  SlideoutPayerValue,
} from './SlideoutHeader'
import type { VisionCheckSummary } from './VisionSlideoutFrame'
import { formatDateShortUs } from '../utils/formatters'
import CheckIdentityEditor from './CheckIdentityEditor'
import { UNSAVED_WARNING } from '../utils/unsavedWork'
import { useUnsavedGuard } from '../utils/useUnsavedGuard'
import { canCorrect } from '../utils/checkCorrection'

interface CheckFailureSlideoutProps {
  check: EligibilityCheckDoc
  /** Shared header data (patient / provider / payer), built in Dashboard via
   *  `buildVisionSummary` so the failure slideout's header matches the Stedi
   *  and vision slideouts exactly. */
  summary: VisionCheckSummary
  onClose: () => void
  /** Open the eligibility form prefilled with patient data so staff can correct + resubmit. */
  onEditAndRetry?: () => void
  /** Org for the in-place correction callable (#314). Absent → editor hidden. */
  orgId?: string
  /** A corrected check was fired AND the panel is done — the parent follows
   *  the flow to it. Not the same as "it happened": see `onCorrectionFired`. */
  onCorrected?: (checkId: string, flowId: string) => void
  /** Fired the moment the corrected check exists, before any clinic-mapping
   *  offer is answered — so closing the panel cannot leave a stale view. */
  onCorrectionFired?: () => void
  /** View-mode staff can look but not edit. */
  canWrite?: boolean
  /** Unsaved-edit signal so closing can warn instead of discarding (#274). */
  onDirtyChange?: (dirty: boolean) => void
  /** Jump to this check's Recovery Loop case (the SMS conversation). Rendered
   *  only when the check is in the recovery loop. */
  onViewRecoveryConversation?: () => void
  /** The host's word on whether this check belongs to a recovery case, when
   *  the doc's own flag can't say (#349): a re-run in a case's chain is never
   *  stamped `inRecoveryLoop`, but opened FROM the case it plainly is one, and
   *  the link is the way back. Omitted → the doc decides. */
  inRecoveryLoop?: boolean
  /** The parent refused a close on this panel's behalf — browser Back, which
   *  the panel can't see. Without it the press is simply swallowed and the
   *  operator gets no reason, which is the dead-press complaint in a new
   *  costume (#274). */
  warnedByParent?: boolean
}

type Tone = 'amber' | 'red' | 'blue'

const RECOVERY_TITLES: Record<RecoveryAction, { title: string; tone: Tone }> = {
  patient_correction: { title: 'Patient data needs correction', tone: 'amber' },
  staff_review: { title: 'Manual review required', tone: 'amber' },
  auto_retry: { title: 'Temporary error — retrying automatically', tone: 'blue' },
  ops_escalation: { title: 'System issue — contact VitalRev AI support', tone: 'red' },
}

const RECOVERY_GUIDANCE: Record<RecoveryAction, string> = {
  patient_correction: "Verify the patient's details below, correct any mistakes, then re-submit the check.",
  staff_review: 'Review the error below. You may need to call the payer or confirm details directly with the patient before retrying.',
  auto_retry: 'This was a transient error (network or payer service). A background job retries these automatically — no action needed unless retries are exhausted.',
  ops_escalation: 'This looks like a system or configuration issue. Please contact VitalRev AI support.',
}

/** Chip text per tone — the at-a-glance "whose problem is this" cue: blue =
 *  the system's (retrying), amber = the staff's, red = VitalRev support's. */
const CHIP_LABELS: Record<Tone, string> = {
  blue: 'Retrying automatically',
  amber: 'Action needed',
  red: 'Contact support',
}

const FIELD_LABELS: Record<PatientField, string> = {
  dateOfBirth: 'Date of birth',
  memberId: 'Member ID',
  payer: 'Payer',
  // #307: a side of the card is not the requirement — the fields are. What this
  // rejection needs off the card is the member ID and the payer
  // (`neededCardFields`), and staff reading this are often the ones typing the
  // message to the patient.
  insuranceCard: 'Insurance card (member ID + plan)',
}

/** Eye icon for vision-vendor failures — matches the VSP/EyeMed slideout header. */
const VisionEyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

function derivePrimaryReason(check: EligibilityCheckDoc): string {
  // Backend-written strings can carry vendor pass-through text (vendor names,
  // doc links, raw API field names) — scrub at the display boundary (#103).
  // Gate on the SCRUBBED value: scrubbing can empty a string (URL-only input),
  // and an empty reason must fall through to the next source instead of
  // rendering blank. Product-owned copy (REASON_COPY, REJECTION_GUIDANCE,
  // the switch below) is rendered as-is.
  const rejectionMessage = scrubVendorText(check.result?.rejection?.message)
  if (rejectionMessage) return rejectionMessage
  const term = readTerminal(check)
  if (term) {
    const reasonCopy = reasonCopyFor(term.reason, check.type)
    if (reasonCopy && term.reason !== 'unknown') return reasonCopy
  }
  const rejectionCategory = check.result?.rejection?.category ?? check.rejectionCategory
  if (rejectionCategory && rejectionCategory !== 'other') {
    return REJECTION_GUIDANCE[rejectionCategory]
  }
  const error = scrubVendorText(check.error)
  if (error) return error
  const errorDetails = scrubVendorText(check.errorDetails)
  if (errorDetails) return errorDetails
  const vspError = scrubVendorText(check.vspError)
  if (vspError) return vspError
  switch (check.outcome) {
    case 'network_error': return 'Could not reach the eligibility service.'
    case 'stedi_error':   return 'The eligibility service returned an error.'
    case 'vsp_error':     return 'The VSP bot failed to retrieve benefits.'
    case 'lookup_error':  return 'The payer rejected this request.'
    default:              return 'This check failed before results could be retrieved.'
  }
}

/** Relative + absolute forms of the check time, so staff can tell a 5-minute-old
 *  failure from a stale one without leaving the panel. */
function formatCheckTime(checkedAt: string): { relative: string; absolute: string } {
  const d = new Date(checkedAt)
  if (isNaN(d.getTime())) return { relative: '', absolute: checkedAt }

  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60_000)
  const diffHour = Math.round(diffMs / 3_600_000)
  const diffDay = Math.round(diffMs / 86_400_000)

  let relative: string
  if (diffMin < 1)       relative = 'just now'
  else if (diffMin < 60) relative = `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`
  else if (diffHour < 24) relative = `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`
  else if (diffDay < 7)  relative = `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
  else                   relative = formatDateShortUs(d)

  const absolute = d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
  return { relative, absolute }
}

export default function CheckFailureSlideout({
  check,
  summary,
  onClose,
  onEditAndRetry,
  onViewRecoveryConversation,
  inRecoveryLoop,
  orgId,
  onCorrected,
  canWrite = true,
  onCorrectionFired,
  onDirtyChange,
  warnedByParent = false,
}: CheckFailureSlideoutProps) {
  const inLoop = inRecoveryLoop ?? !!check.inRecoveryLoop
  // #274: the editor reports typed work; every close path checks it first, so
  // Escape / the overlay / the X cannot be the one that discards a correction.
  // The rule itself lives in useUnsavedGuard — four surfaces mount this editor
  // and they must not each have an opinion about it.
  const {
    guardedClose, setDirty: setEditDirty, dirty: editDirty, showWarning,
  } = useUnsavedGuard(onClose)
  const panelRef = useSlideout(guardedClose)
  const [showSupport, setShowSupport] = useState(false)

  const recovery = check.recoveryAction
  const { title, tone } = recovery ? RECOVERY_TITLES[recovery] : { title: 'Check failed', tone: 'amber' as Tone }
  const primaryReason = derivePrimaryReason(check)
  const notInNetwork = readTerminal(check)?.reason === 'not_in_network'
  const guidance = notInNetwork
    ? 'No patient correction can fix this. Verify with the payer or bill the visit out-of-network.'
    : recovery ? RECOVERY_GUIDANCE[recovery] : 'Review the patient details and re-submit when ready.'
  const fieldsToVerify = check.patientFields ?? []
  // #314: correcting in place is offered for EVERY failure, auto_retry included
  // — a human who can already see the answer should not wait out a retry
  // ladder. The old bounce-to-the-form CTA stays only as the fallback for
  // callers that can't mount the editor (no orgId / no handler).
  const canEditInPlace = Boolean(orgId && onCorrected && canCorrect(check))
  const showRetryButton =
    (notInNetwork || !canEditInPlace) && recovery !== 'auto_retry' && Boolean(onEditAndRetry)
  const isVision = check.type === 'vsp' || check.type === 'eyemed'
  const checkTime = formatCheckTime(check.checkedAt)

  return (
    <>
      <div className="slideout-overlay" onClick={guardedClose} />

      <div
        className="slideout-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Check failed — review needed"
        ref={panelRef}
        tabIndex={-1}
      >
        {/* Same header LAYOUT as the Stedi / vision slideouts (the icon still
            carries the route); the words are failure-specific — a failed check
            must never read as a verification record. */}
        <SlideoutHeaderBar
          icon={isVision ? VisionEyeIcon : <IconShield />}
          title="Review Needed"
          subtitle={title}
          onClose={onClose}
        />

        <div className="slideout-panel__content">
          <SlideoutSummary
            patientName={summary.patientName}
            dob={summary.dob}
            providerName={summary.providerName}
            providerNpi={summary.providerNpi}
            unverified
          />

          <div className="med-divider" />

          <SlideoutPayerGrid
            payer={summary.payerName
              ? <SlideoutPayerValue name={summary.payerName} resolvedFrom={summary.requestPayerName} />
              : undefined}
            memberId={summary.memberId}
          />

          <section className="failure-slideout__section">
            {/* Tone chip — restores the old header icon's "retrying vs act
                now" color cue in the body, keeping the shared header uniform.
                Also softens the "Review Needed" title on auto-retry states,
                where the right message is "nothing to do yet". */}
            <span className={`failure-slideout__chip failure-slideout__chip--${tone}`}>
              {CHIP_LABELS[tone]}
            </span>
            <h3 className="failure-slideout__section-title">
              {recovery === 'auto_retry' ? 'What’s happening' : 'Why this needs review'}
            </h3>
            <p className="failure-slideout__reason">{primaryReason}</p>
            {/* In the body, not the header — the shared header stays identical
                across all slideouts; the time belongs with the failure story. */}
            <p className="failure-slideout__timestamp" title={checkTime.absolute}>
              Checked {checkTime.relative || checkTime.absolute}
              {checkTime.relative && checkTime.absolute && (
                <span className="failure-slideout__timestamp-abs"> · {checkTime.absolute}</span>
              )}
            </p>
          </section>

          <section className="failure-slideout__section">
            <h3 className="failure-slideout__section-title">What to do</h3>
            <p className="failure-slideout__guidance">{guidance}</p>
            {fieldsToVerify.length > 0 && (
              <ul className="failure-slideout__fields">
                {fieldsToVerify.map(f => <li key={f}>{FIELD_LABELS[f]}</li>)}
              </ul>
            )}
            {recovery === 'auto_retry' && typeof check.retryCount === 'number' && (
              <p className="failure-slideout__retry-status">
                <IconRefresh /> Attempt {check.retryCount + 1} of {maxTotalAttempts(check)}
              </p>
            )}
          </section>

          {/* Manual-origin chains carry no SMS candidacy (`smsOutreachAttempted`
              absent) — never hint that a text will be sent for them. */}
          {(inLoop
            || (recovery === 'patient_correction' && check.smsOutreachAttempted !== undefined))
            && onViewRecoveryConversation && (
            <section className="failure-slideout__section">
              <h3 className="failure-slideout__section-title">SMS recovery</h3>
              <p className="failure-slideout__guidance">
                {inLoop
                  ? 'The patient was texted for updated insurance details.'
                  : 'Not in the SMS recovery loop yet — the automated outreach hasn’t picked up this check.'}
              </p>
              <button
                type="button"
                className="failure-slideout__recovery-link"
                onClick={onViewRecoveryConversation}
                disabled={!inLoop}
              >
                View SMS conversation →
              </button>
            </section>
          )}

          <section className="failure-slideout__section">
            <button
              type="button"
              className="failure-slideout__tech-toggle"
              onClick={() => setShowSupport(v => !v)}
              aria-expanded={showSupport}
            >
              Contact support details
              <span className={`failure-slideout__tech-caret${showSupport ? ' failure-slideout__tech-caret--open' : ''}`}>
                <IconChevronDown />
              </span>
            </button>
            {showSupport && (
              <div className="failure-slideout__support">
                <p className="failure-slideout__support-intro">
                  If you need to escalate this check, share the ID below with support.
                </p>
                <dl className="failure-slideout__tech">
                  <dt>Check ID</dt><dd className="failure-slideout__mono">{check.id}</dd>
                </dl>
              </div>
            )}
          </section>

          {/* Inside the scrolling body, where the vision panels host the same
              editor: it scrolls with the sections above it rather than sitting
              pinned under them and squeezing the body into the height left. */}
          {canEditInPlace && (
            <div className="failure-slideout__edit">
              <CheckIdentityEditor
                orgId={orgId as string}
                check={check}
                canWrite={canWrite}
                onDirtyChange={d => { setEditDirty(d); onDirtyChange?.(d) }}
                onFired={onCorrectionFired}
                onCorrected={o => onCorrected?.(o.checkId, o.flowId)}
              />
            </div>
          )}
        </div>

        {/* The hook owns the panel's own close paths; browser Back is refused
            by the PARENT, which this panel cannot see, so its warning has to be
            OR'd in here or that press is silently swallowed (#274). */}
        {(showWarning || (warnedByParent && editDirty)) && (
          <div className="slideout-unsaved" role="status">
            <span>{UNSAVED_WARNING}</span>
            <button className="slideout-unsaved__discard" onClick={onClose}>
              Discard
            </button>
          </div>
        )}

        {showRetryButton && (
          <div className="slideout-panel__footer">
            <button className="slideout-panel__cta" onClick={onEditAndRetry}>
              Edit patient &amp; re-submit
            </button>
          </div>
        )}
      </div>
    </>
  )
}
